import React from "react";
import { Box, Button, Menu, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { NEUTRAL, RADIUS, SHADOW, VIOLET } from "../styles/brandTokens";
import type { Agent } from "../types/integration";
import {
  NORMAL_CHAT_MODE,
  sameTicketChatMode,
  type TicketChatAutomationMode,
  type TicketChatMode,
} from "../utils/ticketChatMode";

export type { TicketChatAutomationMode, TicketChatMode };

const CHIP_LABEL_MAX_WIDTH = 140;
const MENU_LABEL_MAX_WIDTH = 220;

interface ModeOption {
  key: string;
  mode: TicketChatMode;
  label: string;
}

/** Normal, then one Auto Resolver option per enabled Agent. */
export function modeOptions(agents: readonly Agent[]): ModeOption[] {
  const normal: ModeOption = { key: "normal", mode: NORMAL_CHAT_MODE, label: "Normal" };
  if (agents.length === 0) {
    return [normal, { key: "resolver", mode: { capacity: 3, agentId: null }, label: "Resolver" }];
  }
  return [
    normal,
    ...agents.map((agent) => ({
      key: String(agent.id),
      mode: { capacity: 3 as const, agentId: agent.id },
      label: agent.name,
    })),
  ];
}

function optionFor(value: TicketChatMode, options: ModeOption[]): ModeOption {
  const exact = options.find((option) => sameTicketChatMode(option.mode, value));
  if (exact) return exact;
  if (value.capacity === 0) return options[0];
  if (!value.agentId) return options[1];
  return { key: String(value.agentId), mode: value, label: "Resolver" };
}

const MODE_THEMES: Record<
  TicketChatAutomationMode,
  {
    border: string;
    background: string;
    color: string;
    iconColor: string;
    hoverBackground: string;
    hoverBorder: string;
    Icon: typeof TuneIcon;
  }
> = {
  0: {
    border: NEUTRAL.border,
    background: NEUTRAL.surface,
    color: NEUTRAL.textSecondary,
    iconColor: NEUTRAL.textSecondary,
    hoverBackground: NEUTRAL.hover,
    hoverBorder: NEUTRAL.borderStrong,
    Icon: TuneIcon,
  },
  3: {
    border: VIOLET.tint,
    background: VIOLET.wash,
    color: VIOLET.theoText,
    iconColor: VIOLET.theo,
    hoverBackground: VIOLET.tint,
    hoverBorder: VIOLET.tint,
    Icon: BoltOutlinedIcon,
  },
};

interface TicketChatModeSelectorProps {
  value: TicketChatMode | null;
  agents?: readonly Agent[];
  threadMode?: TicketChatMode | null;
  onChange: (value: TicketChatMode) => void;
  disabled?: boolean;
}

const TicketChatModeSelector: React.FC<TicketChatModeSelectorProps> = ({
  value,
  agents = [],
  threadMode = null,
  onChange,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [search, setSearch] = React.useState("");
  const options = React.useMemo(() => modeOptions(agents), [agents]);
  if (value === null) {
    return <Button disabled aria-label="Loading conversation mode">Loading…</Button>;
  }

  const selectedOption = optionFor(value, options);
  const theme = MODE_THEMES[value.capacity];
  const ModeIcon = theme.Icon;
  const close = () => { setAnchorEl(null); setSearch(""); };
  const filteredOptions = options.filter(option => `${option.label} ${option.key}`.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <>
      <Tooltip title={value.capacity === 3 ? `Auto Resolver as ${selectedOption.label}` : "Ticket chat mode"}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
            borderRadius: `${RADIUS.control}px`,
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.background,
            color: theme.color,
            transition: "background-color 0.15s ease, border-color 0.15s ease",
            "&:hover": { backgroundColor: theme.hoverBackground, borderColor: theme.hoverBorder },
          }}
        >
          <Button
            aria-label="Ticket chat mode"
            aria-haspopup="menu"
            aria-expanded={anchorEl ? "true" : undefined}
            disabled={disabled}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
            sx={{
              minWidth: 0,
              px: 0.85,
              py: 0.55,
              color: "inherit",
              textTransform: "none",
              borderRadius: `${RADIUS.control}px`,
              justifyContent: "flex-start",
              lineHeight: 1,
              "&:hover": { backgroundColor: "transparent" },
              "&.Mui-disabled": { color: "inherit", opacity: 0.55 },
            }}
          >
            <ModeIcon sx={{ fontSize: 15, mr: 0.4, color: theme.iconColor }} />
            <Typography
              component="span"
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: 12,
                fontWeight: 500,
                color: "inherit",
                lineHeight: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: CHIP_LABEL_MAX_WIDTH,
              }}
            >
              {selectedOption.label}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 15, ml: 0.35 }} />
          </Button>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        disableRestoreFocus
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: Math.max(anchorEl?.offsetWidth ?? 0, 120),
              mb: 0.75,
              borderRadius: `${RADIUS.card}px`,
              border: `1px solid ${NEUTRAL.border}`,
              boxShadow: SHADOW.popover,
            },
          },
        }}
        MenuListProps={{
          dense: true,
          "aria-label": "Ticket chat mode options",
          onMouseDown: (event) => event.preventDefault(),
        }}
      >
        {options.length > 5 && (
          <Box component="li" role="presentation" sx={{ px: 1, pb: 1 }} onMouseDown={event => event.stopPropagation()}>
            <TextField
              label="Search agents"
              size="small"
              fullWidth
              value={search}
              onChange={event => setSearch(event.target.value)}
              onKeyDown={event => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  event.stopPropagation();
                  event.currentTarget.closest("ul")?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
                } else if (event.key !== "Escape" && event.key !== "Tab") {
                  event.stopPropagation();
                }
              }}
            />
          </Box>
        )}
        {!filteredOptions.length && <Typography component="li" role="status" sx={{ px: 2, py: 1, fontSize: 13 }}>No agents found.</Typography>}
        {filteredOptions.map((option) => (
          <MenuItem
            key={option.key}
            selected={option.key === selectedOption.key}
            onClick={() => {
              onChange(option.mode);
              close();
            }}
            title={option.label}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: 13,
              color: NEUTRAL.text,
              "&.Mui-selected": {
                backgroundColor: MODE_THEMES[option.mode.capacity].background,
                color: MODE_THEMES[option.mode.capacity].color,
              },
              "&.Mui-selected:hover": {
                backgroundColor: MODE_THEMES[option.mode.capacity].hoverBackground,
              },
            }}
          >
            <Box
              component="span"
              sx={{
                maxWidth: MENU_LABEL_MAX_WIDTH,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {option.label}
            </Box>
          </MenuItem>
        ))}
        {threadMode?.capacity === 3 && (
          <Typography
            component="li"
            role="note"
            sx={{
              listStyle: "none",
              maxWidth: MENU_LABEL_MAX_WIDTH + 32,
              px: 2,
              pt: 0.75,
              pb: 0.5,
              mt: 0.5,
              borderTop: `1px solid ${NEUTRAL.border}`,
              fontFamily: "var(--theo-font-sans)",
              fontSize: 11.5,
              lineHeight: 1.35,
              color: NEUTRAL.textSecondary,
            }}
          >
            {`${optionFor(threadMode, options).label} is working this ticket. Another pick ends its run.`}
          </Typography>
        )}
      </Menu>
    </>
  );
};

export default TicketChatModeSelector;
